import { useState, type FC } from 'react';
import { Task } from '../types/Task';
import { PriorityBadge } from './PriorityBadge';
import { getTaskPrioritySortValue } from '../utils/taskPriority';
import { formatTaskDate, formatTaskDateTime, isTaskDueDateOverdue } from '../utils/taskDueDate';
import { TaskActionButtons } from './TaskActionButtons';

const CONTEXT_MENU_IGNORE_SELECTOR = 'button, input, textarea, select, option, a, [role="button"]';

interface TaskTableProps {
  tasks: Task[];
  onDelete: (id: number) => void;
  onStatusChange: (id: number, status: string) => void;
  onViewDetails: (task: Task) => void;
  onEdit: (task: Task) => void;
  onOpenContextMenu?: (task: Task, x: number, y: number) => void;
  selectedTasks: Set<number>;
  onSelectTask: (id: number) => void;
  onSelectAll: () => void;
}

type SortField = 'title' | 'dueDate' | 'updatedAt' | 'status' | 'priority';
type SortDirection = 'asc' | 'desc';

/**
 * TaskTable Component
 * Desktop view for displaying tasks in a sortable table
 * 
 * Features:
 * - Sortable columns (Title, Due Date, Status)
 * - Expandable descriptions
 * - Inline status updates
 * - Edit/Delete actions
 * - Multi-select with checkboxes
 */
export const TaskTable: FC<TaskTableProps> = ({ 
  tasks, 
  onDelete, 
  onStatusChange,
  onViewDetails,
  onEdit,
  onOpenContextMenu,
  selectedTasks,
  onSelectTask,
  onSelectAll
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const toggleRowExpansion = (taskId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedTasks = () => {
    return [...tasks].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'dueDate':
          aValue = a.dueDate ? new Date(a.dueDate).getTime() : 0;
          bValue = b.dueDate ? new Date(b.dueDate).getTime() : 0;
          break;
        case 'updatedAt':
          aValue = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          bValue = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          break;
        case 'status':
          const statusOrder = { 'TODO': 1, 'IN_PROGRESS': 2, 'DONE': 3 };
          aValue = statusOrder[a.status as keyof typeof statusOrder];
          bValue = statusOrder[b.status as keyof typeof statusOrder];
          break;
        case 'priority':
          aValue = getTaskPrioritySortValue(a.priority);
          bValue = getTaskPrioritySortValue(b.priority);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const formatLabels = (labels: string[] | undefined) => {
    if (!labels || labels.length === 0) return '-';
    return labels.join(', ');
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const handleRowContextMenu = (event: React.MouseEvent<HTMLTableRowElement>, task: Task) => {
    if (!onOpenContextMenu) {
      return;
    }

    if (event.target instanceof HTMLElement && event.target.closest(CONTEXT_MENU_IGNORE_SELECTOR)) {
      return;
    }

    event.preventDefault();
    onOpenContextMenu(task, event.clientX, event.clientY);
  };

  const handleRowContextMenuKey = (event: React.KeyboardEvent<HTMLTableRowElement>, task: Task) => {
    if (!onOpenContextMenu) {
      return;
    }

    const isKeyboardContextMenu = event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10');

    if (!isKeyboardContextMenu) {
      return;
    }

    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    onOpenContextMenu(task, rect.left + Math.min(40, rect.width / 2), rect.top + Math.min(40, rect.height / 2));
  };

  const sortedTasks = getSortedTasks();
  
  const allSelected = tasks.length > 0 && tasks.every(task => selectedTasks.has(task.id!));

  return (
    <div className="table-container">
      <table className="task-table">
        <thead>
          <tr>
            <th style={{ width: '50px', textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onSelectAll}
                aria-label="Select all tasks"
              />
            </th>
            <th onClick={() => handleSort('title')} className="sortable" style={{ width: '260px' }}>
              Title{getSortIcon('title')}
            </th>
            <th style={{ width: '320px' }}>Description</th>
            <th onClick={() => handleSort('dueDate')} className="sortable" style={{ width: '110px', textAlign: 'center' }}>
              Due Date{getSortIcon('dueDate')}
            </th>
            <th onClick={() => handleSort('updatedAt')} className="sortable" style={{ width: '190px', textAlign: 'center' }}>
              Updated{getSortIcon('updatedAt')}
            </th>
            <th onClick={() => handleSort('priority')} className="sortable" style={{ width: '120px', textAlign: 'center' }}>
              Priority{getSortIcon('priority')}
            </th>
            <th style={{ width: '200px' }}>Labels</th>
            <th onClick={() => handleSort('status')} className="sortable" style={{ width: '115px', textAlign: 'center' }}>
              Status{getSortIcon('status')}
            </th>
            <th style={{ width: '110px', textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedTasks.map((task) => (
              <tr
                key={task.id!}
                className={selectedTasks.has(task.id!) ? 'selected-row' : ''}
                onContextMenu={(event) => handleRowContextMenu(event, task)}
                onKeyDown={(event) => handleRowContextMenuKey(event, task)}
              >
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedTasks.has(task.id!)}
                    onChange={() => onSelectTask(task.id!)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select task: ${task.title}`}
                  />
                </td>
                <td className="title-cell">
                  <div className="task-title">{task.title}</div>
                </td>
                <td className="description-cell">
                  {!expandedRows.has(task.id!) && task.description && (
                    <>
                      <div className="description-collapsed">
                        {task.description.substring(0, 100)}
                        {task.description.length > 100 && '...'}
                      </div>
                      {task.description.length > 100 && (
                        <button
                          className="expand-btn"
                          onClick={() => toggleRowExpansion(task.id!)}
                        >
                          Show more
                        </button>
                      )}
                    </>
                  )}
                  {expandedRows.has(task.id!) && task.description && (
                    <>
                      <div className="description-expanded">
                        {task.description}
                      </div>
                      <button
                        className="expand-btn"
                        onClick={() => toggleRowExpansion(task.id!)}
                      >
                        Show less
                      </button>
                    </>
                  )}
                  {!task.description && <span style={{ color: '#666' }}>-</span>}
                </td>
                <td className="date-cell centered">
                  <span className={isTaskDueDateOverdue(task.dueDate) ? 'task-date-value is-overdue' : 'task-date-value'}>
                    {formatTaskDate(task.dueDate)}
                    {isTaskDueDateOverdue(task.dueDate) && <span className="task-overdue-badge">Overdue</span>}
                  </span>
                </td>
                <td className="date-cell centered">{formatTaskDateTime(task.updatedAt)}</td>
                <td className="centered">
                  <PriorityBadge priority={task.priority} />
                </td>
                <td className="labels-cell">{formatLabels(task.labels)}</td>
                <td className="centered">
                  <select
                    value={task.status}
                    onChange={(e) => onStatusChange(task.id!, e.target.value)}
                    className="status-dropdown"
                  >
                    <option value="TODO">TODO</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="DONE">Done</option>
                  </select>
                </td>
                <td className="centered">
                  <TaskActionButtons
                    onViewDetails={() => onViewDetails(task)}
                    onEdit={() => onEdit(task)}
                    onDelete={() => {
                      if (window.confirm('Are you sure you want to delete this task?')) {
                        onDelete(task.id!);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};
